import type { World } from 'bitecs';

// === Broad-application generics ===

export interface AnyPoint {
  x: number;
  y: number;
}

export type Eid = number;

export type Colour = { r: number; b: number; g: number };

// === Enums ===

export enum OtherActions {
  NONE,
  HYPERSPACE,
}

export enum PlayerTypes {
  HUMAN,
  BOT_TRIVIAL,
  BOT_EASY,
  BOT_MEDIUM,
  BOT_HARD,
  BOT_INSANE,
}

export enum ObjectTypes {
  NONE,
  DEATHSTAR,
  DEATHBEAM,
  ASTEROID,
  BLACK_HOLE,
  PLANET,
  STAR,
  SUPERGIANT,
  RED_GIANT,
  WHITE_DWARF,
  JOVIAN,
  WORMHOLE,
  BIG_WORMHOLE,
  WHITE_HOLE,
  ANOMALY,
  LOCUS,
  TUNNEL_LOCUS,
  NEUTRON_STAR,
  JET_BLACK_HOLE,
}

export enum Backgrounds {
  NONE,
  STARS,
  DEEPSPACE,
  NEBULAR,
}

export enum ObjectPlacement {
  ANYWHERE = 'anywhere',
  DEAD_CENTER = 'dead_center',
  CLOSE_TO_CENTER = 'close_to_center',
  OUTSKIRTS = 'outskirts',
  SUPERGIANT = 'supergiant'
}

export enum AppModes {
  GAME = "GameMode",
  EDITOR = "EditorMode",
  BACKGROUND = "BackgroundMode"
}

// === Game setup ===

export type GameConfig = {
  justBots?: boolean;
  players?: Array<PlayerSetup>;
  stationPerPlayer?: number;
  items?: Array<ScenarioItem>;
  itemRules?: Array<ScenarioItemRule>;
  background?: Backgrounds;
  stationSize?: number;
  maxItems?: number;
  numItems?: number;
  savedScenario?: SerializedScenario;
};

export type PlayerSetup = {
  id: number;
  type: number;
  col: number;
  difficulty: number;
};

export type GameSetupResult = {
  players: Array<PlayerInfo>;
  objectPlacements: Array<GameObject>;
};

export type ClearanceFunction = (a: number, b: number) => number;

export type GameObject = { x: number; y: number; radius: number; eid: number };
export type UnplacedGameObject = Omit<GameObject, 'x' | 'y'> & { placement?: ObjectPlacement};

export type EntityGenerator<EntityProps = {}> = (
  world: GameWorld,
  pos: AnyPoint,
  props?: EntityProps
) => number;

export type RandomFn = () => number;

export type WeightedChoice<T> = Record<number, T>

export type WorldRandomApi = {
  // Basic random generator, acts like Math.random
  rnd: () => number,

  // Integer between min and max, inclusive
  between: (min: number, max: number) => number;

  // Float between min and max, inclusive only of min
  betweenFloat: (min: number, max: number) => number;

  // Randomly adds or subtracts value with base, number >= 1
  jitter: (base: number, value: number) => number;

  // Randomly adds or subtracts value with base, number < 1
  jitterFloat: (base: number, value: number) => number;

  // Returns true or false based on fraction; so, oneIn(2) will return true 50% of the time
  oneIn: (fraction: number) => boolean;

  // Picks a random element from the provided array
  pickElement: <T>(array: readonly T[]) => T;

  // Accepts weights and values to return
  // For example, [{1: 'foo'}, {2: 'bar'}] will return 'bar' twice as often as 'foo'
  pickChance: <T>(chances: readonly WeightedChoice<T>[]) => T;

  // Generates a random colour
  // Base is the starting colour
  // Bias is how much each value of the starting colour can increase
  // For example, base: rgb(100, 100, 100) and bias rgb(0, 0, 20)
  // can only produce something between 100,100,100 and 100,100,120
  colour: (base: Colour, bias: Colour) => Colour & {ui32: () => number};
};

export type RandomSeed = string | number;

export interface GameWorld extends World {
  time: number;
  delta: number;
  movements: ObjectMovements | null;
  debug: boolean;
  traceBuffer: TraceBuffer | null;
  traceBufferIdx: number;
  seed?: RandomSeed;
  rng: RandomFn;
  random: WorldRandomApi;
  customSeeds: Record<RandomSeed, RandomFn>;
  withCustomSeed: (seed: RandomSeed) => WorldRandomApi;
  getSeed: (eid: number) => RandomSeed;
}

// === In-game stuff ===

export type GameState = {
  lastTurnShots: ObjectMovements | null;
};

export type PlayerInfo = {
  idx: number;
  isAlive: boolean;
  type: PlayerTypes;
  stationEids: Array<number>;
};

export type RawTurn = Pick<TurnInput, 'angle' | 'power'>;

export type TurnInput = {
  stationId: number;
  angle: number;
  power: number;
  otherAction?: OtherActions | null;
  paths?: Array<Array<AnyPoint>>;
};

export type TurnGenerator = (
  world: GameWorld,
  stationId: number,
  gameState: GameState,
  lastTurnInput: TurnInput | null,
  simulator: (t: TurnInput) => Promise<SimShotResult>,
  type?: PlayerTypes,
) => Promise<TurnInput>;

export type ShotInfo = {
  hitsEid: number;
  destructible: boolean;
  willHit: boolean;
  closestDestructible: number;
  closestPoint: AnyPoint;
  closestDist2: number;
  shotDist2: number;
};

export type ObjectMovements = {
  [key: number]: {
    id: number;
    movementTrace: Array<AnyPoint>;
    destroyedTarget: number | null;
  };
};

// === Simulation stuff ===

export type TraceBuffer = {
  x: Int16Array,
  y: Int16Array,
}

export type TransferableTraceBuffer = {
  x: ArrayBuffer;
  y: ArrayBuffer;
}

export type SimShotResult = ShotInfo & {
  collisionT: number | null;
  input: RawTurn;
  shotTrail: Array<AnyPoint>;
  buffer?: TransferableTraceBuffer;
  hitsSelf: boolean;
  pointCount: number;
};

export type TargetCacheEntry = {
  x: number;
  y: number;
  eid: number;
  r: number;
  r2: number;
  breaks: boolean;
  ally?: boolean;
};

export type TargetCache = Array<TargetCacheEntry>;


// === Serialize / deserialize ===

export interface ScenarioItem {
  id: number;
  type: ObjectTypes;
  amount: number;
}

export interface ScenarioItemRule {
  type: ObjectTypes;
  min?: number;
  max?: number;
  n?: number;
  p?: number;
  plc?: ObjectPlacement
}

export type ScenarioType = {
  name: string;
  items: Array<ScenarioItemRule>;
  background?: Backgrounds;
};

export type SerializedComponent = {
  key: string;
  props: {
    [key: string]: number | any;
  };
};

export type SerializedEntity = {
  eid: number;
  components: Array<SerializedComponent>;
};

export type EditorEntity = SerializedEntity & {
  name: string;
}

export type SerializedScenario = {
  name: string;
  background: Backgrounds;
  objects: Array<SerializedEntity>;
}

